'use client';

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal } from '@/components/modals';
import { Button, Typography, Breadcrumbs } from '@/components/ui-components';
import { Dropdown, TextArea } from '@/components/forms/controls';
import { IncidentState } from '@/types/incident';
import { i18n } from '@/config/i18n';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const TypographyError = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;

export interface ChangeIncidentStateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (state: IncidentState, comment: string) => Promise<void>;
  currentState: IncidentState;
  isSubmitting?: boolean;
}

const INCIDENT_STATES: { label: string; value: IncidentState }[] = [
  { label: 'OPEN', value: 'OPEN' },
  { label: 'IN PROGRESS', value: 'IN_PROGRESS' },
  { label: 'RESOLVED', value: 'RESOLVED' },
  { label: 'CLOSED', value: 'CLOSED' },
  { label: 'ON HOLD', value: 'ON_HOLD' },
];

export const ChangeIncidentStateModal: React.FC<ChangeIncidentStateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  currentState,
  isSubmitting = false,
}) => {
  const [selectedState, setSelectedState] = useState<IncidentState>(currentState);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedState(currentState);
      setComment('');
      setError(null);
    }
  }, [isOpen, currentState]);

  const handleSubmit = async () => {
    // Validation
    if (!comment?.trim()) {
      setError(i18n.triage.detail.changeState.validation.commentRequired);
      return;
    }

    if (selectedState === currentState) {
      setError(i18n.triage.detail.changeState.validation.differentState);
      return;
    }

    setError(null);
    try {
      await onSubmit(selectedState, comment?.trim());
      // Close modal on success
      onClose();
      setComment('');
      setSelectedState(currentState);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : i18n.triage.detail.changeState.errors.failed;
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    setComment('');
    setSelectedState(currentState);
    setError(null);
    onClose();
  };

  const dropdownOptions = INCIDENT_STATES.map((state) => ({
    label: state.label,
    value: state.value,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      maxWidth="600px"
    >
      <FormContainer>
        <Breadcrumbs 
          items={[{ label: i18n.triage.detail.changeState.title }]} 
          style={{ padding: 0 }} 
        />

        {error && (
          <TypographyError variant="body-bold" >
            {error}
          </TypographyError>
        )}

        <FormField>
          <Dropdown
            label={i18n.triage.detail.changeState.fields.newState}
            options={dropdownOptions}
            value={selectedState}
            onChange={(value) => setSelectedState(value as IncidentState)}
            required
            placeholder={i18n.triage.detail.changeState.placeholders.selectState}
          />
        </FormField>

        <FormField>
          <TextArea
            label={i18n.triage.detail.changeState.fields.comment+'*'}
            placeholder={i18n.triage.detail.changeState.placeholders.comment}
            value={comment}
            onChange={setComment}
            error={error && !comment?.trim() ? i18n.triage.detail.changeState.validation.commentRequired : undefined}
            rows={4}
          />
        </FormField>

        <ButtonContainer>
          <Button variant="outline" onClick={handleCancel} fullWidth>
            {i18n.common.buttons.cancel}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            fullWidth
            disabled={isSubmitting || selectedState === currentState || !comment?.trim()}
          >
            {isSubmitting ? i18n.triage.detail.changeState.buttons.changing : i18n.triage.detail.changeState.buttons.changeState}
          </Button>
        </ButtonContainer>
      </FormContainer>
    </Modal>
  );
};

